// MenuSubmenu — the flyout list that hangs off a menu row.
//
// There were two copies of this (AssetContextMenu's "Apply to ▸", NewAssetMenu's
// Material / Blueprint families) and both were written the same way:
//
//     position: absolute; left: 100%; top: -4
//
// which places the list correctly and then lets it run straight off the bottom
// of the window. The asset dock lives at the bottom of the screen, so that is
// where every right-click on an asset happens — the common case, not an edge
// one. Measured before the fix, in a 720px-tall window: the Apply-to submenu
// spanned y 612 -> 756 and two of its five rows sat below the window edge with
// no way to reach them. On a real rig (Alessandro's, 9+ mesh slots) most of the
// list was gone.
//
// An `overflowY: auto` on the flyout does not fix that, which is worth stating
// because it looks like it should: the BOX is off-screen, not its content, so
// scrollHeight === clientHeight and there is nothing to scroll.
//
// Two things make it work, and the second is the non-obvious one:
//
//   1. ANCHOR AND CLAMP via AnchoredOverlay — the shared implementation the
//      parent menus already use. It converts out of the UI-scale CSS zoom,
//      clamps on both axes, caps the height at the viewport so a long list
//      scrolls, and re-clamps through a ResizeObserver when rows mount
//      asynchronously.
//
//   2. PORTAL TO <body>. The clamp produces a `position: fixed` box, and the
//      parent menu carries the shared open animation ([data-aos-menu] in
//      styles/index.css) whose transform makes it the containing block for any
//      fixed descendant. Rendered in place, a correctly clamped `left: 740px`
//      painted at 1385 — further off-screen than the bug it was fixing
//      (measured). It also escapes the parent's own `overflow: auto`, which
//      would otherwise clip the flyout to the menu's width.
//
// Leaving the parent's subtree costs two behaviours, so both are handled here
// rather than left to each caller to rediscover:
//
//   - hover-to-keep-open: the flyout is no longer a DOM descendant of the row
//     it hangs off, so it carries the row's enter/leave handlers itself;
//   - the parent's outside-click guard: a mousedown on a portaled row is not
//     inside the menu element. Every such guard must exempt [data-aos-submenu],
//     or the menu unmounts between mousedown and click and the item silently
//     never fires.

import React, { useLayoutEffect, useState } from 'react'
import AnchoredOverlay from './AnchoredOverlay'
import { Z } from '../lib/zLayers'

/**
 * @param {object} props
 * @param {object} props.rowRef ref to the menu row this flyout hangs off
 * @param {boolean} props.open
 * @param {number} [props.minWidth]
 * @param {boolean} [props.flip] open leftwards — for a row near the right edge.
 *   Clamping alone would pull the flyout back over its own parent menu, which
 *   hides the row you are pointing at; flipping keeps them side by side.
 * @param {object} [props.style] the flyout's chrome (colour, border, shadow)
 */
export default function MenuSubmenu({
  rowRef, open, minWidth = 240, flip = false,
  onMouseEnter, onMouseLeave, style, children,
}) {
  // Measured on open, and on open only: a menu does not move while it is up,
  // and re-measuring on every render would fight the clamp.
  const [anchor, setAnchor] = useState(null)
  useLayoutEffect(() => {
    if (!open) { setAnchor(null); return }
    const r = rowRef.current?.getBoundingClientRect()
    if (!r) return
    setAnchor({ x: flip ? r.left - minWidth : r.right, y: r.top })
  }, [open, flip, minWidth, rowRef])

  if (!open || !anchor) return null

  return (
    <AnchoredOverlay
      portal
      x={anchor.x} y={anchor.y} margin={8}
      data-aos-submenu=""
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // The parent menus dismiss on a document click; this list lives outside
      // them now, so it must not read as a click on the page behind.
      onClick={(e) => e.stopPropagation()}
      style={{ minWidth, zIndex: Z.CONTEXT_SUBMENU, ...style }}
    >
      {children}
    </AnchoredOverlay>
  )
}

/**
 * The exemption every menu's outside-click guard needs once its flyouts are
 * portaled. One helper so a new menu cannot forget the reasoning.
 */
export function isInsideSubmenu(target) {
  return !!target?.closest?.('[data-aos-submenu]')
}
